import { useMemo, useState, useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';

import { useTranslate } from 'src/locales';
import { useAppSelector } from 'src/store/store';
import { AddToJobNote } from 'src/services/jobs/jobs.types';

import { RHFMentionTextField } from 'src/components/hook-form';

// ----------------------------------------------------------------------

type Props = {
  /** Called with the note as typed, or undefined while it is blank. Pass a stable setter. */
  onChange: (note: AddToJobNote | undefined) => void;
  helperText?: string;
  disabled?: boolean;
};

type MentionedUser = { id: string };

/**
 * Optional "why are you adding them" note for the add-to-job dialogs (JT1-1738). It is saved as a
 * note on the new application, so it takes @mentions like any other note.
 *
 * The dialogs keep their own state in useState, so the field brings its own form. It uses
 * react-hook-form's bare FormProvider rather than the app's, which renders a <form> the dialogs'
 * submit buttons would then natively submit.
 */
export default function AddToJobNoteField({ onChange, helperText, disabled }: Props) {
  const { t } = useTranslate();

  const users = useAppSelector((state) => state?.data?.users);

  const userOptions = useMemo(() => users?.map((user) => ({
    id: user.id,
    first_name: user.first_name,
    last_name: user.last_name,
  })) || [], [users]);

  const methods = useForm({ defaultValues: { note: '' } });

  // State, not a ref: the text and its mentions change together, and the effect below has to see
  // both of them settle before it reports.
  const [mentions, setMentions] = useState<MentionedUser[]>([]);

  const text = methods.watch('note');

  useEffect(() => {
    onChange(text?.trim()
      ? { text, tagged_accounts: mentions.map(({ id }) => ({ id })) }
      : undefined);
  }, [text, mentions, onChange]);

  return (
    <FormProvider {...methods}>
      <RHFMentionTextField
        name="note"
        label={t('candidates.invites.add_to_job_modal.note.label')}
        placeholder={t('candidates.invites.add_to_job_modal.note.placeholder')}
        helperText={helperText}
        multiline
        rows={3}
        disabled={disabled}
        users={userOptions as any[]}
        onMentionChange={setMentions}
      />
    </FormProvider>
  );
}
